// Compact relative-time formatter used by the admin list views
// (e.g. "Last run" on the Ingestors page). Returns strings like
// "just now", "5m ago", "2h ago", "3d ago", or the absolute date
// for anything > 7 days.
//
// Distinct from the same logic in CoinBalanceChip because that
// formatter is inlined in a player-facing component and doesn't
// have a way to express "never ran" or "error" — both relevant
// for the admin poller-status column.
//
// Keep this in sync with formatTime in
// apps/web/src/features/coins/CoinBalanceChip.tsx (the formatting
// rules are identical, just the failure modes differ).

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 0) return 'just now';   // clock skew — treat as "now"
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Color a relative-time cell by "is the poller healthy?". Returns
 * Tailwind classes for the row's text colour. The thresholds are
 * intentionally generous (any poll in the last 2x the configured
 * interval counts as healthy) because a poller that's "exactly
 * on time" once per interval should look green, not amber.
 */
export function pollerHealthClass(
  lastPollAt: string | null | undefined,
  lastError: string | null | undefined,
  pollIntervalMinutes: number,
): { text: string; dot: string; label: string } {
  if (lastError) {
    return { text: 'text-err', dot: 'bg-err', label: 'Error' };
  }
  if (!lastPollAt) {
    return { text: 'text-ink-muted', dot: 'bg-ink-dim', label: 'Never' };
  }
  const d = new Date(lastPollAt);
  const ageSec = (Date.now() - d.getTime()) / 1000;
  const dueSec = pollIntervalMinutes * 60 * 2; // 2x interval = stale
  if (ageSec <= dueSec) {
    return { text: 'text-accent-success', dot: 'bg-accent-success', label: 'Healthy' };
  }
  return { text: 'text-ink-muted', dot: 'bg-ink-dim', label: 'Stale' };
}
